import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@mui/material';
import RaiseQueryModal from '../components/queries/RaiseQueryModal';

const PowerBIDashboard = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalParams, setModalParams] = useState({});
  const iframeRef = useRef(null);
  const originalWindowOpenRef = useRef(null);

  useEffect(() => {
    // Make the parent content area overflow hidden so iframe fills it completely
    const parent = document.querySelector('[data-content-area]');
    if (parent) {
      const originalOverflow = parent.style.overflow;
      parent.style.overflow = 'hidden';
      
      return () => {
        parent.style.overflow = originalOverflow;
      };
    }
  }, []);

  // Parse URL and extract query parameters
  const parseQueryUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    try {
      // Handle both absolute and relative URLs
      let urlObj;
      try {
        urlObj = new URL(url);
      } catch (e) {
        // If URL is relative, create a full URL using current origin
        urlObj = new URL(url, window.location.origin);
      }
      
      const pathname = urlObj.pathname;
      
      // Check if URL matches the query form pattern
      // Supports both relative paths and full URLs (including external domains)
      const isQueryFormUrl = 
        pathname.includes('/dashboard/queries/raise/new') ||
        pathname.includes('/queries/raise/new');
      
      if (!isQueryFormUrl) {
        return null;
      }

      const params = {};
      urlObj.searchParams.forEach((value, key) => {
        params[key] = value;
      });

      return params;
    } catch (error) {
      // Fallback: try to extract from string pattern matching
      if (url.includes('/queries/raise/new') || url.includes('/dashboard/queries/raise/new')) {
        const params = {};
        
        // Extract query string
        const queryStringMatch = url.match(/[?&](.+)$/);
        if (queryStringMatch) {
          const queryString = queryStringMatch[1];
          const pairs = queryString.split('&');
          
          pairs.forEach(pair => {
            const [key, value = ''] = pair.split('=');
            if (key) {
              try {
                params[decodeURIComponent(key)] = decodeURIComponent(value);
              } catch (e) {
                params[key] = value;
              }
            }
          });
        }
        
        return Object.keys(params).length > 0 ? params : null;
      }
      return null;
    }
  };

  useEffect(() => {
    // Handle intercepted navigation
    const handleInterceptedNavigation = (url) => {
      const params = parseQueryUrl(url);
      if (params) {
        // Prevent default navigation
        // Open modal with parsed parameters
        setModalParams({
          visualDescription: params.visualDescription || '',
          region: params.region || '',
          project: params.project || '',
          projectName: params.projectName || '',
          queryType: params.queryType || '',
          priority: params.priority || '',
          assigneeId: params.assigneeId || '',
        });
        setModalOpen(true);
        return true; // Indicates we handled it
      }
      return false; // Let default behavior proceed
    };

    // Override window.open to intercept new tab/window opens
    originalWindowOpenRef.current = window.open;
    
    window.open = function(url, target, features) {
      // Check if this is a query form URL
      if (url && handleInterceptedNavigation(url)) {
        // Return a mock window object to prevent errors
        return {
          close: () => {},
          closed: false,
          focus: () => {},
        };
      }
      
      // For other URLs, use original window.open
      if (originalWindowOpenRef.current) {
        return originalWindowOpenRef.current.call(window, url, target, features);
      }
    };

    // Listen for postMessage events from Power BI iframe
    const handleMessage = (event) => {
      // Power BI might send navigation events via postMessage
      // We check for various possible message formats
      if (event.data) {
        const data = event.data;
        
        // Check if message contains a URL
        if (typeof data === 'string' && data.includes('queries/raise/new')) {
          handleInterceptedNavigation(data);
        } else if (data.url && typeof data.url === 'string') {
          handleInterceptedNavigation(data.url);
        } else if (data.type === 'navigation' && data.url) {
          handleInterceptedNavigation(data.url);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Also listen for beforeunload to catch navigation attempts
    // This is a fallback for cases where window.open doesn't catch it
    const handleBeforeUnload = (event) => {
      // This won't prevent navigation but can help detect it
      // We rely more on window.open override
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Restore original window.open
      if (originalWindowOpenRef.current) {
        window.open = originalWindowOpenRef.current;
      }
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
    setModalParams({});
  };

  const handleModalSuccess = () => {
    setModalOpen(false);
    setModalParams({});
  };

  return (
    <>
      <Box 
        sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
        }}
      >
        <iframe
          ref={iframeRef}
          title="Pravah dashboard - Dev"
          width="100%"
          height="100%"
          src="https://app.powerbi.com/view?r=eyJrIjoiMzIxZmRjYWItOGQyMy00M2Q0LWFhZjEtYzVjOTUyNWZkNTI2IiwidCI6ImUxNTVmZWViLTUzZmQtNDg5Zi1iNjE2LTRiMjliYzdjMDQ3MCJ9"
          frameBorder="0"
          allowFullScreen={true}
          style={{ 
            border: 'none',
            display: 'block',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            padding: 0,
          }}
        />
      </Box>
      
      <RaiseQueryModal
        open={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        {...modalParams}
      />
    </>
  );
};

export default PowerBIDashboard;

